/**
 * Withdrawal Referral Screen
 * Withdrawal of referral earnings with bank transfer
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  TouchableOpacity,
  ScrollView,
  Alert,
  ActivityIndicator,
  TextInput,
} from 'react-native';
import Icon from '../components/Icon';
import { useTheme } from '../context/ThemeContext';
import { authAPI } from '../services/authApi';
import { userAPI, referralAPI } from '../services/api';

const WithdrawalReferralScreen = ({ navigation }) => {
  const { colors, isDarkMode } = useTheme();
  
  // State Management
  const [referralBalance, setReferralBalance] = useState(0);
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('BANK');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [earningHistory, setEarningHistory] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  
  // Bank Details
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [upiId, setUpiId] = useState('');
  const [usdWalletAddress, setUsdWalletAddress] = useState('');

  // Withdrawal Methods (Same as WithdrawalScreen)
  const withdrawalMethods = [
    {
      id: '1',
      name: 'Bank Transfer',
      apiMethod: 'BANK',
      icon: 'business',
      color: '#4A90E2',
      fee: '$0',
      minWithdraw: '$100',
      processingTime: '3-5 days',
      requiresBankDetails: true,
    },
    {
      id: '2',
      name: 'UPI',
      apiMethod: 'UPI',
      icon: 'phone-portrait',
      color: '#00E676',
      fee: '$0',
      minWithdraw: '$10',
      processingTime: 'Instant',
      requiresUPI: true,
    },
    {
      id: '3',
      name: 'USDT (TRC20)',
      apiMethod: 'USDT',
      icon: 'logo-bitcoin',
      color: '#00E676',
      fee: 'Network fee',
      minWithdraw: '$50',
      processingTime: '1-6 hours',
      requiresWalletAddress: true,
    },
  ];

  useEffect(() => {
    console.log('💳 WithdrawalReferralScreen mounted');
    console.log('🔐 Starting data fetch...');
    fetchData();
    
    return () => {
      console.log('💳 WithdrawalReferralScreen unmounted');
    };
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      
      console.log('🔄 WithdrawalReferralScreen: Fetching referral balance data...');
      
      try {
        const response = await userAPI.getReferralBalance();
        console.log('📊 Full API Response:', JSON.stringify(response, null, 2));
        
        // Check different possible response structures
        let apiData = null;
        let referralBalanceValue = 0;
        
        if (response?.data) {
          console.log('✅ Response data exists:', response.data);
          
          // If response.data has success and data properties
          if (response.data.success && response.data.data) {
            apiData = response.data.data;
            referralBalanceValue = apiData.referralBalance || 0;
            console.log('✅ Using response.data.data structure');
          } 
          // If response.data directly contains referralBalance
          else if (response.data.referralBalance !== undefined) {
            apiData = response.data;
            referralBalanceValue = response.data.referralBalance;
            console.log('✅ Using response.data structure');
          }
          // If response.data is the success/data wrapper
          else if (response.data.data && response.data.data.referralBalance !== undefined) {
            apiData = response.data.data;
            referralBalanceValue = response.data.data.referralBalance;
            console.log('✅ Using nested data structure');
          }
        }
        
        console.log('💰 Extracted Referral Balance:', referralBalanceValue);
        console.log('📄 API Data Object:', apiData);
        
        if (apiData) {
          // Set referral balance
          setReferralBalance(referralBalanceValue || 0);
          
          // Set withdrawal history
          setWithdrawalHistory(apiData.withdrawalHistory || []);
          
          // Set earning history  
          setEarningHistory(apiData.earningHistory || []);
          
          // Set user info
          setUserInfo(apiData.user || null);
          
          console.log('✅ Data loaded successfully:');
          console.log('💰 Balance:', referralBalanceValue);
          console.log('📤 Withdrawals:', apiData.withdrawalHistory?.length || 0);
          console.log('💸 Earnings:', apiData.earningHistory?.length || 0);
        } else {
          console.log('⚠️ Could not find referral balance in response, using fallback');
          // Fallback to default values
          setReferralBalance(0);
          setWithdrawalHistory([]);
          setEarningHistory([]);
          setUserInfo(null);
        }
      } catch (apiError) {
        console.error('❌ API Error fetching referral balance:', apiError.message);
        console.log('Error response:', apiError.response?.data);
        console.log('Error status:', apiError.response?.status);
        
        // Set fallback data on error - DON'T redirect to login
        setReferralBalance(0);
        setWithdrawalHistory([]);
        setEarningHistory([]);
        setUserInfo(null);
        
        // Show error but allow user to stay on screen
        console.log('⚠️ Using fallback data, screen will still render');
      }
    } catch (error) {
      console.error('❌ Unexpected error in fetchData:', error);
      // Set safe defaults
      setReferralBalance(0);
      setWithdrawalHistory([]);
      setEarningHistory([]);
      setUserInfo(null);
    } finally {
      setIsLoading(false);
      console.log('✅ fetchData completed, isLoading set to false');
    }
  };

  const getMinWithdrawalAmount = () => {
    switch (selectedMethod) {
      case 'BANK':
        return 100;
      case 'UPI':
        return 10;
      case 'USDT':
        return 50;
      default:
        return 100;
    }
  };

  const validateForm = () => {
    const amount = parseFloat(withdrawalAmount);
    
    if (!withdrawalAmount || amount <= 0) {
      Alert.alert('Error', 'Please enter a valid withdrawal amount');
      return false;
    }
    
    if (amount > referralBalance) {
      Alert.alert('Error', `Insufficient balance. Available: $${referralBalance.toFixed(2)}`);
      return false;
    }
    
    const minAmount = getMinWithdrawalAmount();
    if (amount < minAmount) {
      Alert.alert('Error', `Minimum withdrawal amount is $${minAmount}`);
      return false;
    }
    
    // Method-specific validation
    if (selectedMethod === 'BANK') {
      if (!accountHolderName.trim()) {
        Alert.alert('Error', 'Please enter account holder name');
        return false;
      }
      if (!accountNumber.trim() || accountNumber.length < 10 || accountNumber.length > 18) {
        Alert.alert('Error', 'Please enter a valid account number (10-18 digits)');
        return false;
      }
      if (!ifscCode.trim() || !/^[A-Z0-9]{11}$/.test(ifscCode)) {
        Alert.alert('Error', 'Please enter a valid IFSC code (e.g., SBIN0001234)');
        return false;
      }
      if (!bankName.trim()) {
        Alert.alert('Error', 'Please enter bank name');
        return false;
      }
    } else if (selectedMethod === 'UPI') {
      if (!upiId.trim() || !/^[a-zA-Z0-9.-_]+@[a-zA-Z]+$/.test(upiId)) {
        Alert.alert('Error', 'Please enter a valid UPI ID (e.g., username@bank)');
        return false;
      }
    } else if (selectedMethod === 'USDT') {
      if (!usdWalletAddress.trim() || usdWalletAddress.length < 34 || usdWalletAddress.length > 42) {
        Alert.alert('Error', 'Please enter a valid USDT wallet address (TRC20)');
        return false;
      }
    }
    
    return true;
  };

  const handleWithdrawal = async () => {
    if (!validateForm()) return;
    
    try {
      setIsSubmitting(true);
      
      // Build withdrawal data for referral withdrawal
      const withdrawalData = {
        amount: parseFloat(withdrawalAmount),
        withdrawalMethod: selectedMethod === 'BANK' ? 'BANK_TRANSFER' : selectedMethod,
      };

      // Add method-specific fields
      if (selectedMethod === 'BANK') {
        withdrawalData.bankDetails = {
          accountHolderName,
          accountNumber,
          ifscCode,
          bankName
        };
      } else if (selectedMethod === 'UPI') {
        withdrawalData.upiId = upiId;
      } else if (selectedMethod === 'USDT') {
        withdrawalData.usdtAddress = usdWalletAddress;
      }
      
      console.log('Submitting referral withdrawal:', withdrawalData);
      
      // Use the specific referral withdrawal endpoint
      const response = await userAPI.requestReferralWithdrawal(withdrawalData);
      
      const currentMethod = withdrawalMethods.find(m => m.apiMethod === selectedMethod);
      
      Alert.alert(
        '✅ Referral Withdrawal Request Submitted',
        `Your referral withdrawal request for $${parseFloat(withdrawalAmount).toFixed(2)} via ${currentMethod.name} has been submitted for admin approval.\n\nProcessing Time: ${currentMethod.processingTime}\n\nYou will receive a confirmation email shortly.`,
        [
          {
            text: 'OK',
            onPress: () => {
              resetForm();
              fetchData();
            }
          }
        ]
      );
    } catch (error) {
      console.error('Referral Withdrawal error:', error);
      let errorMessage = 'Failed to submit referral withdrawal request. Please try again.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid referral withdrawal request. Please check your details.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Session expired. Please login again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Referral withdrawal not allowed. Please verify your account.';
      }
      
      Alert.alert('Error', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setWithdrawalAmount('');
    setSelectedMethod('BANK');
    setAccountHolderName('');
    setAccountNumber('');
    setIfscCode('');
    setBankName('');
    setUpiId('');
    setUsdWalletAddress('');
  };

  const quickAmounts = selectedMethod === 'BANK' 
    ? ['100', '250', '500', 'All']
    : selectedMethod === 'UPI'
    ? ['10', '50', '100', 'All']
    : ['50', '100', '200', 'All']; // USDT

  const getQuickAmount = (amount) => {
    if (amount === 'All') {
      return Math.floor(referralBalance).toString();
    }
    return amount;
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <StatusBar barStyle={isDarkMode ? "light-content" : "dark-content"} backgroundColor={colors.background} />
      
      {/* Header */}
      <View style={[styles.header, { backgroundColor: colors.background, borderBottomColor: colors.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Icon name="arrow-back" size={24} color={colors.primary} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: colors.text }]}>Withdraw Referral Earnings</Text>
        <TouchableOpacity style={styles.headerIcon} onPress={fetchData}>
          <Icon name="refresh" size={24} color={colors.primary} />
        </TouchableOpacity>
      </View>

      <ScrollView showsVerticalScrollIndicator={false}>
        {isLoading ? (
          <View style={{ padding: 40, alignItems: 'center' }}>
            <ActivityIndicator size="large" color={colors.primary} />
            <Text style={{ color: colors.textSecondary, marginTop: 10 }}>Loading...</Text>
          </View>
        ) : (
          <>
            {/* Balance Card */}
            <View style={[styles.balanceCard, { backgroundColor: `${colors.primary}1A`, borderColor: `${colors.primary}33` }]}>
              <View style={styles.balanceContent}>
                <Icon name="wallet" size={32} color={colors.primary} />
                <View style={styles.balanceInfo}>
                  <Text style={[styles.balanceLabel, { color: colors.textSecondary }]}>Referral Balance</Text>
                  <Text style={[styles.balanceValue, { color: colors.primary }]}>${referralBalance.toFixed(2)}</Text>
                  {userInfo && (
                    <Text style={[styles.userInfo, { color: colors.textTertiary }]}>
                      {userInfo.name} • {userInfo.email}
                    </Text>
                  )}
                </View>
              </View>
            </View>

            {/* Information Box */}
            <View style={[styles.infoBox, { backgroundColor: `${colors.success}15`, borderColor: `${colors.success}33` }]}>
              <Icon name="information-circle" size={20} color={colors.success} />
              <Text style={[styles.infoText, { color: colors.textSecondary }]}>
                {selectedMethod === 'BANK' && 'Minimum withdrawal: $100 • Processing time: 3-5 business days'}
                {selectedMethod === 'UPI' && 'Minimum withdrawal: $10 • Processing time: Instant'}
                {selectedMethod === 'USDT' && 'Minimum withdrawal: $50 • Processing time: 1-6 hours'}
              </Text>
            </View>

            {/* Withdrawal Amount Section */}
            <View style={styles.section}>
              <Text style={[styles.sectionTitle, { color: colors.text }]}>Withdrawal Amount</Text>
              <View style={[styles.amountInputContainer, { backgroundColor: colors.cardBackground, borderColor: colors.border }]}>
                <Text style={[styles.currencySymbol, { color: colors.primary }]}>$</Text>
                <TextInput
                  style={[styles.amountInput, { color: colors.text }]}
                  placeholder="Enter amount"
                  placeholderTextColor={colors.textTertiary}
                  keyboardType="numeric"
                  value={withdrawalAmount}
                  onChangeText={setWithdrawalAmount}
                />
              </View>

              {/* Quick Amount Buttons */}
              <View style={styles.quickAmountsContainer}>
                {quickAmounts.map((amount) => (
                  <TouchableOpacity
                    key={amount}
                    style={[
                      styles.quickAmountButton,
                      { backgroundColor: colors.cardBackground, borderColor: colors.border },
                      withdrawalAmount === getQuickAmount(amount) && { backgroundColor: `${colors.primary}33`, borderColor: colors.primary }
                    ]}
                    onPress={() => setWithdrawalAmount(getQuickAmount(amount))}
                  >
                    <Text
                      style={[
                        styles.quickAmountText,
                        { color: colors.textSecondary },
                        withdrawalAmount === getQuickAmount(amount) && { color: colors.primary, fontWeight: '700' }
                      ]}
                    >
                      {amount === 'All' ? 'All' : `$${amount}`}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Withdrawal Method Selection */}
            <View style={styles.section}>
              <Text style={[styles.sectionTitle, { color: colors.text }]}>Select Withdrawal Method</Text>
              {withdrawalMethods.map((method) => (
                <TouchableOpacity
                  key={method.id}
                  style={[
                    styles.methodCardFull,
                    { backgroundColor: colors.cardBackground, borderColor: colors.border },
                    selectedMethod === method.apiMethod && { borderColor: method.color, borderWidth: 2 }
                  ]}
                  onPress={() => {
                    setSelectedMethod(method.apiMethod);
                    setWithdrawalAmount('');
                  }}
                >
                  <View style={[styles.methodIconLarge, { backgroundColor: `${method.color}25` }]}>
                    <Icon name={method.icon} size={32} color={method.color} />
                  </View>
                  
                  <View style={styles.methodContentFull}>
                    <Text style={[styles.methodNameLarge, { color: colors.text }]}>{method.name}</Text>
                    <View style={styles.methodMetaRow}>
                      <Icon name="time" size={14} color={colors.textSecondary} />
                      <Text style={[styles.methodTime, { color: colors.textSecondary }]}>{method.processingTime}</Text>
                    </View>
                    <Text style={[styles.methodMinAmountLarge, { color: colors.textSecondary }]}>Min: {method.minWithdraw}</Text>
                  </View>

                  {selectedMethod === method.apiMethod && (
                    <View style={[styles.selectedBadgeLarge, { backgroundColor: method.color }]}>
                      <Icon name="checkmark" size={16} color="#fff" />
                    </View>
                  )}
                </TouchableOpacity>
              ))}
            </View>

            {/* Bank Details Form (for Bank Transfer) */}
            {selectedMethod === 'BANK' && (
              <View style={styles.section}>
                <Text style={[styles.sectionTitle, { color: colors.text }]}>Bank Details</Text>
                
                <View style={[styles.inputContainer, { backgroundColor: colors.cardBackground, borderColor: colors.border }]}>
                  <Icon name="person" size={20} color={colors.textSecondary} />
                  <TextInput
                    style={[styles.textInput, { color: colors.text }]}
                    placeholder="Account Holder Name"
                    placeholderTextColor={colors.textTertiary}
                    value={accountHolderName}
                    onChangeText={setAccountHolderName}
                  />
                </View>
                
                <View style={[styles.inputContainer, { backgroundColor: colors.cardBackground, borderColor: colors.border }]}>
                  <Icon name="home" size={20} color={colors.textSecondary} />
                  <TextInput
                    style={[styles.textInput, { color: colors.text }]}
                    placeholder="Bank Name"
                    placeholderTextColor={colors.textTertiary}
                    value={bankName}
                    onChangeText={setBankName}
                  />
                </View>
                
                <View style={[styles.inputContainer, { backgroundColor: colors.cardBackground, borderColor: colors.border }]}>
                  <Icon name="card" size={20} color={colors.textSecondary} />
                  <TextInput
                    style={[styles.textInput, { color: colors.text }]}
                    placeholder="Account Number (10-18 digits)"
                    placeholderTextColor={colors.textTertiary}
                    keyboardType="numeric"
                    value={accountNumber}
                    onChangeText={setAccountNumber}
                    maxLength={18}
                  />
                </View>
                
                <View style={[styles.inputContainer, { backgroundColor: colors.cardBackground, borderColor: colors.border }]}>
                  <Icon name="code" size={20} color={colors.textSecondary} />
                  <TextInput
                    style={[styles.textInput, { color: colors.text }]}
                    placeholder="IFSC Code (e.g., SBIN0001234)"
                    placeholderTextColor={colors.textTertiary}
                    value={ifscCode}
                    onChangeText={(text) => setIfscCode(text.toUpperCase())}
                    autoCapitalize="characters"
                    maxLength={11}
                  />
                </View>
              </View>
            )}

            {/* UPI ID Form (for UPI) */}
            {selectedMethod === 'UPI' && (
              <View style={styles.section}>
                <Text style={[styles.sectionTitle, { color: colors.text }]}>UPI Details</Text>
                
                <View style={[styles.inputContainer, { backgroundColor: colors.cardBackground, borderColor: colors.border }]}>
                  <Icon name="phone-portrait" size={20} color={colors.textSecondary} />
                  <TextInput
                    style={[styles.textInput, { color: colors.text }]}
                    placeholder="UPI ID (e.g., username@bank)"
                    placeholderTextColor={colors.textTertiary}
                    value={upiId}
                    onChangeText={setUpiId}
                    autoCapitalize="none"
                    keyboardType="email-address"
                  />
                </View>
              </View>
            )}

            {/* USDT Wallet Address Form (for USDT) */}
            {selectedMethod === 'USDT' && (
              <View style={styles.section}>
                <Text style={[styles.sectionTitle, { color: colors.text }]}>USDT Wallet Details</Text>
                
                <View style={[styles.inputContainer, { backgroundColor: colors.cardBackground, borderColor: colors.border }]}>
                  <Icon name="logo-bitcoin" size={20} color={colors.textSecondary} />
                  <TextInput
                    style={[styles.textInput, { color: colors.text }]}
                    placeholder="USDT Address (TRC20)"
                    placeholderTextColor={colors.textTertiary}
                    value={usdWalletAddress}
                    onChangeText={setUsdWalletAddress}
                    autoCapitalize="none"
                  />
                </View>
                
                <View style={[styles.infoBox, { backgroundColor: `${colors.warning}15`, borderColor: `${colors.warning}33` }]}>
                  <Icon name="warning" size={16} color={colors.warning} />
                  <Text style={[styles.infoText, { color: colors.textSecondary, fontSize: 12 }]}>
                    Use only TRC20 network address. Do not use other networks.
                  </Text>
                </View>
              </View>
            )}

            {/* Warning Notice */}
            <View style={[styles.warningNotice, { backgroundColor: `${colors.warning}1A`, borderColor: `${colors.warning}33` }]}>
              <Icon name="warning" size={20} color={colors.warning} />
              <Text style={[styles.warningText, { color: colors.textSecondary }]}>
                {selectedMethod === 'BANK' && 'Ensure your bank details are correct. Incorrect details may result in delays or failed withdrawals.'}
                {selectedMethod === 'UPI' && 'Ensure your UPI ID is correct and belongs to your verified account.'}
                {selectedMethod === 'USDT' && 'Use only TRC20 network. Do not use other blockchain networks.'}
              </Text>
            </View>

            {/* Withdraw Button */}
            <TouchableOpacity
              style={[
                styles.withdrawButton,
                { backgroundColor: colors.primary },
                (!selectedMethod || !withdrawalAmount || isSubmitting) && { opacity: 0.5 }
              ]}
              disabled={!selectedMethod || !withdrawalAmount || isSubmitting}
              onPress={handleWithdrawal}
            >
              {isSubmitting ? (
                <ActivityIndicator size="small" color={colors.text} />
              ) : (
                <>
                  <Icon name="cash-outline" size={24} color={colors.text} />
                  <Text style={[styles.withdrawButtonText, { color: colors.text }]}>
                    Request Withdrawal ${withdrawalAmount || '0.00'}
                  </Text>
                </>
              )}
            </TouchableOpacity>

            <View style={{ height: 100 }} />
          </>
        )}
      </ScrollView>
    </View>
  );
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'approved':
    case 'completed':
      return { bg: '#00E67620', text: '#00E676' };
    case 'rejected':
    case 'failed':
      return { bg: '#FF444420', text: '#FF4444' };
    case 'pending':
    case 'processing':
      return { bg: '#FFB00020', text: '#FFB000' };
    default:
      return { bg: '#6B728020', text: '#6B7280' };
  }
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 50,
    paddingBottom: 16,
    borderBottomWidth: 1,
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    flex: 1,
    textAlign: 'center',
  },
  headerIcon: {
    padding: 4,
  },
  balanceCard: {
    marginHorizontal: 16,
    marginTop: 20,
    marginBottom: 12,
    padding: 20,
    borderRadius: 16,
    borderWidth: 1,
  },
  balanceContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  balanceInfo: {
    flex: 1,
  },
  balanceLabel: {
    fontSize: 12,
    marginBottom: 4,
    fontWeight: '500',
  },
  balanceValue: {
    fontSize: 28,
    fontWeight: '700',
  },
  userInfo: {
    fontSize: 12,
    marginTop: 4,
  },
  infoBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginHorizontal: 16,
    marginBottom: 20,
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
  },
  infoText: {
    flex: 1,
    fontSize: 12,
  },
  section: {
    paddingHorizontal: 16,
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  amountInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderWidth: 1,
    marginBottom: 16,
  },
  currencySymbol: {
    fontSize: 24,
    fontWeight: '700',
    marginRight: 8,
  },
  amountInput: {
    flex: 1,
    fontSize: 24,
    fontWeight: '700',
  },
  quickAmountsContainer: {
    flexDirection: 'row',
    gap: 10,
  },
  quickAmountButton: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
  },
  quickAmountText: {
    fontSize: 13,
    fontWeight: '600',
  },
  methodCard: {
    borderRadius: 12,
    padding: 14,
    borderWidth: 1,
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 150,
  },
  methodsGrid: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 20,
  },
  methodCardFull: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    gap: 14,
  },
  methodIconLarge: {
    width: 60,
    height: 60,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
  },
  methodContentFull: {
    flex: 1,
  },
  methodNameLarge: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 6,
  },
  methodMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8,
  },
  selectedBadgeLarge: {
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
  },
  methodIcon: {
    width: 52,
    height: 52,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  methodInfo: {
    flex: 1,
  },
  methodName: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 2,
    marginTop: 10,
  },
  methodMinAmount: {
    fontSize: 13,
    fontWeight: '700',
    marginTop: 8,
  },
  methodTime: {
    fontSize: 11,
    marginTop: 2,
  },
  selectedBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  methodDesc: {
    fontSize: 12,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginBottom: 12,
    borderWidth: 1,
    gap: 12,
  },
  textInput: {
    flex: 1,
    fontSize: 15,
    fontWeight: '500',
  },
  historyCard: {
    borderRadius: 12,
    padding: 14,
    marginBottom: 10,
  },
  historyHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  historyAmount: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 2,
  },
  historyDate: {
    fontSize: 11,
  },
  historyMethod: {
    fontSize: 12,
    fontWeight: '500',
    marginBottom: 2,
  },
  adminNotes: {
    fontSize: 11,
    marginTop: 4,
    fontStyle: 'italic',
  },
  historyContent: {
    flex: 1,
  },
  earningHeader: {
    marginBottom: 6,
  },
  earningAmount: {
    fontSize: 15,
    fontWeight: '700',
    marginBottom: 2,
  },
  earningDate: {
    fontSize: 11,
  },
  earningDescription: {
    fontSize: 12,
    lineHeight: 16,
  },
  moreEarnings: {
    fontSize: 12,
    fontStyle: 'italic',
  },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
  },
  statusText: {
    fontSize: 11,
    fontWeight: '600',
  },
  warningNotice: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    marginHorizontal: 16,
    marginBottom: 24,
    padding: 14,
    borderRadius: 12,
    borderWidth: 1,
  },
  warningText: {
    flex: 1,
    fontSize: 12,
    lineHeight: 18,
  },
  withdrawButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    marginHorizontal: 16,
    paddingVertical: 16,
    borderRadius: 12,
    marginBottom: 16,
  },
  withdrawButtonText: {
    fontSize: 16,
    fontWeight: '700',
  },
});

export default WithdrawalReferralScreen;
